import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { AiSearchPatternUpdateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputText } from "../../../common/controls";
import { InputStringList } from "../../../common/controls";
import { InputNumber } from "../../../common/controls";
import { InputBoolean } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type AiSearchPatternUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  name: string;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  nameError?: string;
  description: string;
  descriptionSelectedValue?: { displayId: string; id: string } | null;
  onDescriptionChange?: (value: string) => void;
  descriptionError?: string;
  pattern: string;
  patternSelectedValue?: { displayId: string; id: string } | null;
  onPatternChange?: (value: string) => void;
  patternError?: string;
  pathTypes: string[];
  pathTypesSelectedValue?: { displayId: string; id: string } | null;
  onPathTypesChange?: (value: string[]) => void;
  pathTypesError?: string;
  tools: string[];
  toolsSelectedValue?: { displayId: string; id: string } | null;
  onToolsChange?: (value: string[]) => void;
  toolsError?: string;
  effectiveness: number | undefined;
  effectivenessSelectedValue?: { displayId: string; id: string } | null;
  onEffectivenessChange?: (value: number | undefined) => void;
  effectivenessError?: string;
  evaluationCount: number;
  evaluationCountSelectedValue?: { displayId: string; id: string } | null;
  onEvaluationCountChange?: (value: number) => void;
  evaluationCountError?: string;
  successCount: number;
  successCountSelectedValue?: { displayId: string; id: string } | null;
  onSuccessCountChange?: (value: number) => void;
  successCountError?: string;
  sourceEvalIds: string[];
  sourceEvalIdsSelectedValue?: { displayId: string; id: string } | null;
  onSourceEvalIdsChange?: (value: string[]) => void;
  sourceEvalIdsError?: string;
  proposedBy: string;
  proposedBySelectedValue?: { displayId: string; id: string } | null;
  onProposedByChange?: (value: string) => void;
  proposedByError?: string;
  active: boolean;
  activeSelectedValue?: { displayId: string; id: string } | null;
  onActiveChange?: (value: boolean) => void;
  activeError?: string;
  priority: number;
  prioritySelectedValue?: { displayId: string; id: string } | null;
  onPriorityChange?: (value: number) => void;
  priorityError?: string;
  aiSearchPatternUpdateStore: AiSearchPatternUpdateStore;
};

export const AiSearchPatternUpdateForm: React.FC<AiSearchPatternUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      name = "",
      nameError,
      onNameChange = () => {},
      description = "",
      descriptionError,
      onDescriptionChange = () => {},
      pattern = "",
      patternError,
      onPatternChange = () => {},
      pathTypes = [],
      pathTypesError,
      onPathTypesChange = () => {},
      tools = [],
      toolsError,
      onToolsChange = () => {},
      effectiveness = undefined,
      effectivenessError,
      onEffectivenessChange = () => {},
      evaluationCount = 0,
      evaluationCountError,
      onEvaluationCountChange = () => {},
      successCount = 0,
      successCountError,
      onSuccessCountChange = () => {},
      sourceEvalIds = [],
      sourceEvalIdsError,
      onSourceEvalIdsChange = () => {},
      proposedBy = "",
      proposedByError,
      onProposedByChange = () => {},
      active = false,
      activeError,
      onActiveChange = () => {},
      priority = 0,
      priorityError,
      onPriorityChange = () => {},
      aiSearchPatternUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="AiSearchPattern"
              mode="update"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "name":
                    onNameChange?.(valor);
                    break;
                  case "description":
                    onDescriptionChange?.(valor);
                    break;
                  case "pattern":
                    onPatternChange?.(valor);
                    break;
                  case "pathTypes":
                    onPathTypesChange?.(valor);
                    break;
                  case "tools":
                    onToolsChange?.(valor);
                    break;
                  case "effectiveness":
                    onEffectivenessChange?.(valor);
                    break;
                  case "evaluationCount":
                    onEvaluationCountChange?.(valor);
                    break;
                  case "successCount":
                    onSuccessCountChange?.(valor);
                    break;
                  case "sourceEvalIds":
                    onSourceEvalIdsChange?.(valor);
                    break;
                  case "proposedBy":
                    onProposedByChange?.(valor);
                    break;
                  case "active":
                    onActiveChange?.(valor);
                    break;
                  case "priority":
                    onPriorityChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={aiSearchPatternUpdateStore.getCardErrors("null")}
              title={langStore.groupAiSearchPatternLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={name}
                  error={Boolean(nameError)}
                  warning={
                    !nameError &&
                    Boolean(aiSearchPatternUpdateStore.warnings["name"])
                  }
                  helperText={
                    nameError ||
                    aiSearchPatternUpdateStore.warnings["name"]?.message
                  }
                  label={langStore.AiSearchPatternNameLabel}
                  onChange={(value: string) => onNameChange(value)}
                  description={langStore.AiSearchPatternNameDescription}
                />
                <InputText
                  value={description}
                  error={Boolean(descriptionError)}
                  warning={
                    !descriptionError &&
                    Boolean(aiSearchPatternUpdateStore.warnings["description"])
                  }
                  helperText={
                    descriptionError ||
                    aiSearchPatternUpdateStore.warnings["description"]?.message
                  }
                  label={langStore.AiSearchPatternDescriptionLabel}
                  onChange={(value: string) => onDescriptionChange(value)}
                />
                <InputText
                  value={pattern}
                  error={Boolean(patternError)}
                  warning={
                    !patternError &&
                    Boolean(aiSearchPatternUpdateStore.warnings["pattern"])
                  }
                  helperText={
                    patternError ||
                    aiSearchPatternUpdateStore.warnings["pattern"]?.message
                  }
                  label={langStore.AiSearchPatternPatternLabel}
                  onChange={(value: string) => onPatternChange(value)}
                  description={langStore.AiSearchPatternPatternDescription}
                />
                <InputStringList
                  value={pathTypes}
                  error={Boolean(pathTypesError)}
                  helperText={
                    pathTypesError ||
                    aiSearchPatternUpdateStore.warnings["pathTypes"]?.message
                  }
                  label={langStore.AiSearchPatternPathTypesLabel}
                  description={langStore.AiSearchPatternPathTypesDescription}
                  onChange={(value: string[]) => onPathTypesChange(value)}
                />
                <InputStringList
                  value={tools}
                  error={Boolean(toolsError)}
                  helperText={
                    toolsError ||
                    aiSearchPatternUpdateStore.warnings["tools"]?.message
                  }
                  label={langStore.AiSearchPatternToolsLabel}
                  description={langStore.AiSearchPatternToolsDescription}
                  onChange={(value: string[]) => onToolsChange(value)}
                />
                <InputNumber
                  value={effectiveness}
                  nullable={true}
                  error={Boolean(effectivenessError)}
                  warning={
                    !effectivenessError &&
                    Boolean(
                      aiSearchPatternUpdateStore.warnings["effectiveness"]
                    )
                  }
                  helperText={
                    effectivenessError ||
                    aiSearchPatternUpdateStore.warnings["effectiveness"]
                      ?.message
                  }
                  label={langStore.AiSearchPatternEffectivenessLabel}
                  decimalScale={2}
                  nullText={langStore.optionalFieldHint}
                  onChange={(value: number | undefined) =>
                    onEffectivenessChange(value)
                  }
                />
                <InputNumber
                  value={evaluationCount}
                  error={Boolean(evaluationCountError)}
                  warning={
                    !evaluationCountError &&
                    Boolean(
                      aiSearchPatternUpdateStore.warnings["evaluationCount"]
                    )
                  }
                  helperText={
                    evaluationCountError ||
                    aiSearchPatternUpdateStore.warnings["evaluationCount"]
                      ?.message
                  }
                  label={langStore.AiSearchPatternEvaluationCountLabel}
                  decimalScale={0}
                  onChange={(value: number) => onEvaluationCountChange(value)}
                />
                <InputNumber
                  value={successCount}
                  error={Boolean(successCountError)}
                  warning={
                    !successCountError &&
                    Boolean(aiSearchPatternUpdateStore.warnings["successCount"])
                  }
                  helperText={
                    successCountError ||
                    aiSearchPatternUpdateStore.warnings["successCount"]?.message
                  }
                  label={langStore.AiSearchPatternSuccessCountLabel}
                  decimalScale={0}
                  onChange={(value: number) => onSuccessCountChange(value)}
                />
                <InputStringList
                  value={sourceEvalIds}
                  error={Boolean(sourceEvalIdsError)}
                  helperText={
                    sourceEvalIdsError ||
                    aiSearchPatternUpdateStore.warnings["sourceEvalIds"]
                      ?.message
                  }
                  label={langStore.AiSearchPatternSourceEvalIdsLabel}
                  onChange={(value: string[]) => onSourceEvalIdsChange(value)}
                />
                <InputString
                  value={proposedBy}
                  error={Boolean(proposedByError)}
                  warning={
                    !proposedByError &&
                    Boolean(aiSearchPatternUpdateStore.warnings["proposedBy"])
                  }
                  helperText={
                    proposedByError ||
                    aiSearchPatternUpdateStore.warnings["proposedBy"]?.message
                  }
                  label={langStore.AiSearchPatternProposedByLabel}
                  onChange={(value: string) => onProposedByChange(value)}
                  nullText={langStore.optionalFieldHint}
                />
                <InputBoolean
                  value={active}
                  error={Boolean(activeError)}
                  helperText={
                    activeError ||
                    aiSearchPatternUpdateStore.warnings["active"]?.message
                  }
                  label={langStore.AiSearchPatternActiveLabel}
                  onChange={(value: boolean) => onActiveChange(value)}
                />
                <InputNumber
                  value={priority}
                  error={Boolean(priorityError)}
                  warning={
                    !priorityError &&
                    Boolean(aiSearchPatternUpdateStore.warnings["priority"])
                  }
                  helperText={
                    priorityError ||
                    aiSearchPatternUpdateStore.warnings["priority"]?.message
                  }
                  label={langStore.AiSearchPatternPriorityLabel}
                  decimalScale={0}
                  onChange={(value: number) => onPriorityChange(value)}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
